import React, { useRef } from 'react'

import { MemberRole } from 'features/team/model/types/members'
import { getUserAvatarUrl, getUserRole } from 'features/user/model/selectors/userSelectors'
import { useUserMenuItems } from 'features/user/ui/UserMenu'
import { useAppSelector } from 'shared/config/store'
import { useRouteTitle } from 'shared/hooks/useRouteTitle'
import { Avatar, Button, Dropdown, UserOutlined } from 'shared/ui'
import { hasAccess } from 'shared/utils/access'
import styled from 'styled-components'
import { useLocation, useNavigate } from 'react-router-dom'
import { ROUTES } from 'shared/constants/routesPaths'

import { NotificationIcon } from '../notifications/NotificationIcon'
import { SearchInput } from '../search'

interface HeaderProps {
  breadcrumbs?: React.ReactNode;
  onShare?: () => void;
  onUpload?: (files: File[]) => void;
}

export const Header: React.FC<HeaderProps> = ({
  breadcrumbs,
  onUpload,
}) => {
  const { title, showBreadcrumbs } = useRouteTitle()
  const menuItems = useUserMenuItems()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const userRole = useAppSelector(getUserRole)
  const location = useLocation()
  const navigate = useNavigate()
  const userAvatarUrl = useAppSelector(getUserAvatarUrl)

  const isAiAssistant = location.pathname.startsWith(ROUTES.dashboard.aiAssistant)

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // clone e.target.files to files array
    const files = e.target.files ? Array.from(e.target.files) : []
    if (files && files.length > 0) {
      onUpload && onUpload(files)
    }
    // Reset the input value so the same file can be selected again
    e.target.value = ''
  }

  return (
    <SHeaderContainer>
      <SLeftSection>
        {title && (
          <STitle>
            {title}
          </STitle>
        )}

        {showBreadcrumbs && breadcrumbs && (
          <SBreadcrumbs>
            {breadcrumbs}
          </SBreadcrumbs>
        )}
      </SLeftSection>

      <SRightSection>
        {!isAiAssistant && (
          <SearchInput />
        )}

        {/* <SShareButton type="default"
          onClick={onShare}
        >
          Share
        </SShareButton> */}

        {!isAiAssistant && (
          <SFileUploadWrapper>
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileChange}
              multiple
              style={{ display: 'none' }}
            />

            {hasAccess(userRole, [MemberRole.ADMIN, MemberRole.OWNER, MemberRole.USER]) && (
              <Button
                type="primary"
                onClick={() => fileInputRef.current?.click()}
              >
              Upload
              </Button>
            )}
          </SFileUploadWrapper>
        )}

        {isAiAssistant && (
          <Button
            type="primary"
            onClick={() => navigate(ROUTES.dashboard.aiAssistant)}
          >
            New chat
          </Button>
        )}

        <SNotificationIcon />

        <Dropdown menu={{ items: menuItems }}
          trigger={['click']}
        >
          <SUserAvatar
            src={!userAvatarUrl ? undefined : userAvatarUrl}
            icon={!userAvatarUrl && <UserOutlined />}
          />
        </Dropdown>
      </SRightSection>
    </SHeaderContainer>
  )
}

const SHeaderContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 24px;
  background-color: white;
`

const SLeftSection = styled.div`
  display: flex;
  align-items: center;
`

const SRightSection = styled.div`
  display: flex;
  align-items: center;

  .ant-avatar{
    margin-left: 16px;
  }

  .ant-upload-wrapper{
    margin-left: 12px;
  }
`

const SBreadcrumbs = styled.div`
  display: flex;
  align-items: center;
  color: #8c8c8c;
  font-size: 14px;
  letter-spacing: 0.15px;
  margin-left: 4px;
  cursor: pointer;
`

const SFileUploadWrapper = styled.div`
  margin-left: 12px;
`

const SUserAvatar = styled(Avatar)`
  background-color: #0000000F;
  cursor: pointer;
  min-width: 32px;
  color: #000000E0;
`

const SNotificationIcon = styled(NotificationIcon)`
  margin-left: 16px;
`

const STitle = styled.p`
font-size: 16px;
font-style: normal;
font-weight: 500;
line-height: 24px;
letter-spacing: 0.15px;
`
